import React, { useEffect, useState } from 'react';
import { CreditCard, ClipboardCheck, FileWarning, Megaphone } from 'lucide-react';
import { payoutAPI, announcementAPI } from '@/lib/api';
import { AttentionCard } from './AttentionCard';

/**
 * AccountingAttentionRequired — top Command Center band on the Accounting
 * dashboard. Mirrors the AttentionRequired pattern used by KR (20.3),
 * Agency (20.5), and Agent (20.6), scoped to accounting-operations only.
 *
 * NO Production / Sales / Revenue / Commission / Performance-ranking
 * concepts introduced (per Phase 20.7 guardrails). Every card counts
 * OPERATIONAL items — records the accountant needs to action — not $
 * amounts.
 *
 * Data sources (all pre-existing, agency-scoped by backend permissions):
 *   1. Pending Payment Information → GET /payout/eft?status=Pending Review
 *                                    Count of EFT records awaiting review.
 *   2. Pending Reviews             → GET /payout/statements?status=Draft
 *                                    Statements awaiting release.
 *   3. Outstanding Requests        → GET /payout/ledger-stats.by_status.unassigned
 *                                    (falls back to 'Unmatched'/'unassigned'/'pending'
 *                                    keys — accepts either casing).
 *   4. System Notifications        → GET /announcements (active only).
 *
 * Rules: zero-count cards omitted; empty state = "Nothing requires your
 * attention 🎉"; returns null during load — no empty-state flash.
 */

const readCount = (arrayLike) => {
  if (!arrayLike) return 0;
  if (Array.isArray(arrayLike)) return arrayLike.length;
  if (Array.isArray(arrayLike?.items)) return arrayLike.items.length;
  if (Array.isArray(arrayLike?.statements)) return arrayLike.statements.length;
  return 0;
};

const readBucket = (byStatus, keys) => {
  for (const k of keys) {
    const v = byStatus?.[k];
    if (v?.count) return v.count;
  }
  return 0;
};

export const AccountingAttentionRequired = ({ parentLoading = false }) => {
  const [state, setState] = useState({ eft: 0, statements: 0, unassigned: 0, notifs: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [eftRes, stmtRes, statsRes, annRes] = await Promise.all([
        payoutAPI.eft.list({ status: 'Pending Review' }).catch(() => null),
        payoutAPI.statements.list({ status: 'Draft' }).catch(() => null),
        payoutAPI.ledger.stats().catch(() => null),
        announcementAPI.list().catch(() => null),
      ]);
      if (cancelled) return;
      setState({
        eft: readCount(eftRes?.data),
        statements: readCount(stmtRes?.data),
        unassigned: readBucket(statsRes?.data?.by_status, ['unassigned', 'Unassigned', 'unmatched', 'Unmatched', 'pending', 'Pending']),
        notifs: Array.isArray(annRes?.data) ? annRes.data.filter((a) => a?.active).length : 0,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (parentLoading || loading) return null;

  const cards = [
    {
      key: 'eft',
      title: 'Pending Payment Information',
      count: state.eft,
      description: 'EFT records awaiting review before payouts can flow.',
      icon: CreditCard,
      href: '/accounting/eft',
      actionLabel: 'Review EFT records',
      severity: 'warning',
      testId: 'accounting-attention-card-eft',
    },
    {
      key: 'reviews',
      title: 'Pending Reviews',
      count: state.statements,
      description: 'Payout statements sitting in draft status.',
      icon: ClipboardCheck,
      href: '/accounting/compensation',
      actionLabel: 'Review statements',
      severity: 'info',
      testId: 'accounting-attention-card-reviews',
    },
    {
      key: 'outstanding',
      title: 'Outstanding Requests',
      count: state.unassigned,
      description: 'Ledger entries still unassigned to a statement.',
      icon: FileWarning,
      href: '/accounting/compensation',
      actionLabel: 'View ledger',
      severity: 'critical',
      testId: 'accounting-attention-card-outstanding',
    },
    {
      key: 'notifications',
      title: 'System Notifications',
      count: state.notifs,
      description: 'Active company announcements.',
      icon: Megaphone,
      href: '/announcements',
      actionLabel: 'View announcements',
      severity: 'neutral',
      testId: 'accounting-attention-card-notifications',
    },
  ].filter((c) => (c.count || 0) > 0);

  if (cards.length === 0) {
    return (
      <section className="mb-8" data-testid="accounting-attention-required-empty">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Attention Required
        </h2>
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-8 text-center">
          <p className="text-base font-heading font-semibold text-slate-800">
            Nothing requires your attention <span aria-hidden="true">🎉</span>
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Payment info, reviews, and ledger inbox are all clear.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="accounting-attention-required">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
        Attention Required
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <AttentionCard
            key={c.key}
            title={c.title}
            count={c.count}
            description={c.description}
            icon={c.icon}
            href={c.href}
            actionLabel={c.actionLabel}
            severity={c.severity}
            testId={c.testId}
          />
        ))}
      </div>
    </section>
  );
};

export default AccountingAttentionRequired;
